import { cn } from '@/utils/utils';
import * as React from 'react';

interface SimpleSwitchProps {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled?: boolean;
  className?: string;
  /** Custom primary color for preview purposes */
  primaryColor?: string;
  /** Custom secondary color for preview purposes */
  secondaryColor?: string;
}

const SimpleSwitch = React.forwardRef<HTMLButtonElement, SimpleSwitchProps>(
  ({ checked, onCheckedChange, disabled = false, className, primaryColor, secondaryColor, ...props }, ref) => {
    // Use inline styles if custom colors are provided (for preview), otherwise use CSS variables
    const buttonStyle: React.CSSProperties | undefined =
      primaryColor || secondaryColor
        ? {
            backgroundColor: secondaryColor || 'var(--secondary)',
          }
        : undefined;

    const thumbStyle: React.CSSProperties | undefined =
      primaryColor || secondaryColor
        ? {
            backgroundColor: primaryColor || 'var(--primary)',
          }
        : undefined;

    return (
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        disabled={disabled}
        className={cn(
          'peer focus-visible:ring-ring focus-visible:ring-offset-background inline-flex h-[1.4rem] w-[2.8rem] shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
          // Only apply CSS variable classes if no custom colors are provided
          !primaryColor && !secondaryColor && 'bg-[var(--secondary)]',
          !primaryColor && !secondaryColor && 'dark:bg-[var(--primary)]',
          className
        )}
        style={buttonStyle}
        data-state={checked ? 'checked' : 'unchecked'}
        onClick={() => !disabled && onCheckedChange(!checked)}
        ref={ref}
        {...props}
      >
        <span
          className={cn(
            'pointer-events-none flex h-[1.2rem] w-[1.2rem] items-center justify-center rounded-full shadow-lg ring-0 transition-transform',
            // Only apply CSS variable classes if no custom colors are provided
            !primaryColor && !secondaryColor && 'bg-[var(--primary)]',
            !primaryColor && !secondaryColor && 'dark:bg-[var(--secondary)]',
            checked ? 'translate-x-[1.4rem]' : 'translate-x-0'
          )}
          style={thumbStyle}
          data-state={checked ? 'checked' : 'unchecked'}
        />
      </button>
    );
  }
);

SimpleSwitch.displayName = 'SimpleSwitch';

export { SimpleSwitch };
